import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import RfpDraftModal from "../components/RfpDraftModal";
import { Upload, FileText, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Piping", "Instrumentation", "Heavy Haul", "HSE", "Labor"];

export default function RFPUpload() {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [operator, setOperator] = useState("");
  const [deadline, setDeadline] = useState("");
  const [priority, setPriority] = useState("normal");
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState(-1);
  const [draftOpen, setDraftOpen] = useState(false);
  const [aiDraftFile, setAiDraftFile] = useState(null);
  const inputRef = useRef();
  const navigate = useNavigate();

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const applyAiDraft = (draft) => {
    setTitle(draft.title || "");
    setOperator(draft.operator || "");
    // Build a synthetic text file from the AI draft for upload
    const lines = [
      `RFP Title: ${draft.title || ""}`,
      `Operator: ${draft.operator || ""}`,
      `Scope: ${draft.scope_summary || ""}`,
      "",
      "Line items:",
      ...(draft.line_items || []).map(li =>
        `[${li.category}] ${li.description} | Qty: ${li.quantity} ${li.unit} | Spec: ${li.spec || "N/A"}`
      ),
    ].join("\n");
    const blob = new Blob([lines], { type: "text/plain" });
    const synthetic = new File([blob], "ai_drafted_rfp.txt", { type: "text/plain" });
    setFile(synthetic);
    setAiDraftFile(synthetic);
    toast.success("AI draft loaded — set deadline and submit");
  };

  const submit = async () => {
    if (!file || !title || !operator || !deadline) {
      toast.error("All fields required");
      return;
    }
    setUploading(true);
    setStage(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("operator", operator);
    fd.append("deadline", deadline);
    fd.append("priority", priority);

    // Animate the 5-stage AI processing
    const stageTimer = setInterval(() => {
      setStage(s => Math.min(s + 1, CATEGORIES.length - 1));
    }, 900);

    try {
      const { data } = await api.post("/rfps/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      // give animation a moment to complete
      setTimeout(() => {
        clearInterval(stageTimer);
        toast.success("RFP uploaded · AI is parsing");
        navigate(`/bid/${data.id}`);
      }, 1200);
    } catch (e) {
      clearInterval(stageTimer);
      setUploading(false);
      setStage(-1);
      toast.error("Upload failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="rfp-upload-page">
        <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-2">RFP Intake</div>
        <div className="flex justify-between items-start flex-wrap gap-3 mb-3">
          <h1 className="font-display text-5xl">Upload Operator RFP</h1>
          <button onClick={() => setDraftOpen(true)} data-testid="ai-draft-btn"
            className="btn-tactile inline-flex items-center gap-2 text-xs">
            <Wand2 className="w-3.5 h-3.5 text-amber-500" /> AI Draft Mode
          </button>
        </div>
        <p className="text-text-secondary mb-8">Drop a PDF or Excel. Our AI agent will parse line items and auto-create Sub-RFQs for all 5 vendor categories.</p>

        <div className="tray p-7 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono-data text-[10px] tracking-[0.2em] uppercase text-text-secondary mb-2">Bid Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} data-testid="rfp-title-input"
                placeholder="Permian Tie-In 2026" className="w-full tray-inset px-4 py-3 bg-transparent font-mono-data text-sm outline-none focus:ring-1 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block font-mono-data text-[10px] tracking-[0.2em] uppercase text-text-secondary mb-2">Operator</label>
              <input value={operator} onChange={e => setOperator(e.target.value)} data-testid="rfp-operator-input"
                placeholder="Apache Corp" className="w-full tray-inset px-4 py-3 bg-transparent font-mono-data text-sm outline-none focus:ring-1 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block font-mono-data text-[10px] tracking-[0.2em] uppercase text-text-secondary mb-2">Bid Deadline</label>
              <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} data-testid="rfp-deadline-input"
                className="w-full tray-inset px-4 py-3 bg-transparent font-mono-data text-sm outline-none focus:ring-1 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block font-mono-data text-[10px] tracking-[0.2em] uppercase text-text-secondary mb-2">Priority</label>
              <div className="flex gap-2">
                {["normal", "high"].map(p => (
                  <button key={p} onClick={() => setPriority(p)} data-testid={`priority-${p}-btn`}
                    className={`tray-inset flex-1 py-3 font-mono-data text-xs uppercase tracking-widest transition ${priority === p ? "ring-2 ring-amber-500 text-amber-500" : "text-text-secondary"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDrop={onDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            data-testid="rfp-dropzone"
            className="tray-inset cursor-pointer p-10 border-2 border-dashed border-white/10 hover:border-amber-500/50 transition text-center"
          >
            <input ref={inputRef} type="file" accept=".pdf,.xlsx,.xls,.txt" hidden onChange={e => setFile(e.target.files?.[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-6 h-6 text-amber-500" />
                <div>
                  <div className="font-mono-data text-sm">{file.name}</div>
                  <div className="font-mono-data text-[10px] text-text-secondary">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
            ) : (
              <div>
                <Upload className="w-8 h-8 text-text-secondary/60 mx-auto mb-3" />
                <div className="font-display text-lg">Drop RFP here</div>
                <div className="font-mono-data text-[10px] text-text-secondary tracking-widest uppercase mt-1">PDF · XLSX · TXT</div>
              </div>
            )}
          </div>

          {/* Processing stages */}
          {uploading && (
            <div className="tray-inset p-5 space-y-2" data-testid="ai-processing">
              <div className="font-mono-data text-[10px] tracking-[0.25em] uppercase text-amber-500 mb-3">
                AI Agent · Categorization
              </div>
              {CATEGORIES.map((c, i) => (
                <div key={c} className="flex items-center gap-3 font-mono-data text-xs">
                  {i <= stage ? (
                    <span className="text-amber-500">[OK]</span>
                  ) : i === stage + 1 ? (
                    <Loader2 className="w-3 h-3 animate-spin text-amber-500" />
                  ) : (
                    <span className="text-text-secondary">[··]</span>
                  )}
                  <span className={i <= stage ? "text-white" : "text-text-secondary"}>
                    Parsing {c} line items...
                  </span>
                </div>
              ))}
            </div>
          )}

          <button onClick={submit} disabled={uploading} data-testid="rfp-submit-btn"
            className="btn-tactile btn-amber w-full">
            {uploading ? "Processing..." : "Submit & Auto-Generate Sub-RFQs"}
          </button>
        </div>

        <RfpDraftModal open={draftOpen} onClose={() => setDraftOpen(false)} onApply={applyAiDraft} />
      </div>
    </Layout>
  );
}
